import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { getToken } from 'services/Auth/AuthService';
import styles from './SearchTextFieldCustom.scss';
import { getSearchTextFieldTheme } from 'config/MaterialTheme';
import { getElementID, getMergedClassNames } from 'utils/HTMLUtils';
import { CustomTextField } from 'components/CustomTextField';
import searchIcon from './../../../assets/images/svg/search-icon.svg';
import { useDispatch } from 'react-redux';
import { clearNameAction } from 'store/modules/AssessoresActions';
import { getEndpointBff } from 'services/projeto';
import api from 'services/api';

const SearchTextFieldCustom = ({
  id,
  disabled,
  placeholder,
  searchValueSelector,
  updateStatePropsNewTab,
  selectedCategory,
  onValueChange,
  resetPage,
}) => {
  const hasMounted = useRef(false);
  const [value, setValue] = useState(searchValueSelector || '');
  const [suggestions, setSuggestions] = useState([]);
  const valueRef = useRef('');
  const [, setEnterPressed] = useState(false);
  const dispatch = useDispatch();

  const resetValueForCategory = () => {
    if (hasMounted.current) {
      setValue('');
    } else {
      hasMounted.current = true;
    }
  };

  useEffect(() => {
    resetValueForCategory();
  }, [selectedCategory]);

  useEffect(() => {
    return () => {
      dispatch(clearNameAction());
    };
  }, [dispatch]);

  const rootRef = useRef(null);
  const fetchSuggestions = useCallback((filtro, pesquisarPor) => {
    const accessToken = getToken();
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
    const url = `${getEndpointBff()}/sugestao?filtro=${filtro}&pesquisarPor=${pesquisarPor}`;

    api.get(url, config)
      .then((response) => {
        setSuggestions(response.data);
      })
      .catch((error) => console.error('Erro ao buscar sugestões:', error));
  }, []);

  function debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        timeout = null;
        func.apply(this, args);
      }, wait);
    };
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchSuggestions = useCallback(
    debounce(fetchSuggestions, 500),
    [fetchSuggestions]
  );

  const handleInputChange = (event) => {
    const newValue = event.target.value.toUpperCase();
    setValue(newValue);
    valueRef.current = newValue;
    updateStatePropsNewTab(newValue);
    if (newValue.trim() === '') {
      setSuggestions([]);
    } else if (newValue.length >= 3) {
      debouncedFetchSuggestions(newValue, selectedCategory);
    }
    setEnterPressed(false);
  };

  const handleFormSubmit = () => {
    const currentValue = valueRef.current;
    if (currentValue.trim() === '') {
      setValue('');
      updateStatePropsNewTab('');
      dispatch(clearNameAction());
      onValueChange('');
      setSuggestions([]);
      resetPage();
      return;
    }
    if (resetPage) {
      resetPage();
    }
    if (!suggestions.includes(currentValue)) {
      onValueChange(currentValue);
    }
    setSuggestions([]);
    setEnterPressed(true);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setSuggestions([]);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const rootClassName = styles.rootClassName;
  return (
    <div
      ref={rootRef}
      className={getMergedClassNames([
        rootClassName,
        disabled ? `${rootClassName}-disabled` : '',
      ])}
    >
      <MuiThemeProvider theme={getSearchTextFieldTheme()}>
        <CustomTextField
          id={id ? getElementID(id, 'text-field') : null}
          className={`${rootClassName}-text-field`}
          value={value}
          onChange={handleInputChange}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleFormSubmit();
            }
          }}
          InputProps={{
            endAdornment: (
              <button
                type='button'
                id={id ? getElementID(id, 'button') : null}
                className={`${rootClassName}-search-button`}
                disabled={disabled}
                onClick={handleFormSubmit}
              >
                <img
                  src={searchIcon}
                  alt='icone-pesquisa'
                  loading='lazy'
                  className={`${rootClassName}-search-icon`}
                />
              </button>
            ),
            disabled,
            placeholder: placeholder || '',
          }}
        />
        {suggestions.length > 0 && (
          <div className={`${rootClassName}-suggestions`}>
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(suggestion);
                  setSuggestions([]);
                  onValueChange(suggestion);
                  resetPage();
                }}
                style={{ cursor: 'pointer' }}
              >
                {suggestion}
              </div>
            ))}
          </div>
        )}
      </MuiThemeProvider>
    </div>
  );
};

SearchTextFieldCustom.propTypes = {
  id: PropTypes.string,
  onSearchClick: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  selectedCategory: PropTypes.number,
  currentPage: PropTypes.number,
};

export default SearchTextFieldCustom;
